<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="loadError" class="dw-empty">Could not load vehicle maintenance data.</div>
    <!-- one all-clear when both halves are empty. two headings each
         followed by its own "nothing here" line said the same thing twice
         and filled the card with the absence of news. -->
    <template v-else-if="!notReadyDepartures.length && !items.length">
      <div class="dw-empty">
        Every vehicle departing in the next {{ departureWindowHours }} hours is ready, and none are out of service.
      </div>
    </template>
    <template v-else>
      <div class="dw-section-label">Not Ready, Departing Within {{ departureWindowHours }} Hours</div>
      <div v-if="!notReadyDepartures.length" class="dw-empty" style="margin-bottom: 10px">All clear.</div>
      <div v-else class="dw-table-wrap" style="margin-bottom: 14px">
        <table class="dw-table">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Wash Status</th>
              <th>Maint Status</th>
              <th>Departs In</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in notReadyDepartures" :key="'nr-' + row.name" @click="goToVsm">
              <td>{{ row.name }}</td>
              <td>{{ washLabel(row) }}</td>
              <td>{{ maintLabel(row) }}</td>
              <td><span class="dw-chip" :class="departsChip(row)">{{ departsLabel(row) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="dw-section-label" style="margin-top: 12px">Out of Service</div>
      <div v-if="!items.length" class="dw-empty">None.</div>
      <template v-else>
        <div class="dw-stat-grid" style="margin-bottom: 10px">
          <div class="dw-stat-tile">
            <div class="dw-stat-tile-label">Out of Service</div>
            <div class="dw-stat-tile-value">{{ items.length }}</div>
          </div>
          <div class="dw-stat-tile">
            <div class="dw-stat-tile-label">Past Due</div>
            <div class="dw-stat-tile-value">{{ pastDueCount }}</div>
          </div>
        </div>
        <div class="dw-table-wrap">
          <table class="dw-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>OOS Date</th>
                <th>Days Past Due</th>
                <th>Open Issues</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topRows" :key="row.vehicle + row.oos_date" @click="goToMaintSummary">
                <td>{{ row.vehicle }}</td>
                <td>{{ formatDate(row.oos_date) }}</td>
                <td>{{ row.days_past_due || 0 }}</td>
                <td>{{ row.issues || row.work_orders || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { get_vehicle_maint_summary, get_vehicle_info } from '../../networking/apis'
import { statusLabel } from '../../utils/vehicle_status_labels'
import { vsmRouteName } from '../../utils/vsm_access'

// a coach fleet's departures cluster in the early morning, so a 4-hour
// window (the original) was empty for most of the working day on every
// tenant: check it at 4pm and the next departure is typically 12+ hours
// out, so the widget reported "all clear" precisely when someone still had
// time to act on a vehicle that isn't ready. 12 hours covers the next
// departure wave from any point in the day -- look in the afternoon and you
// see tomorrow morning's, look in the morning and you see the rest of today.
const DEPARTURE_WINDOW_HOURS = 12
const DEPARTURE_WINDOW_MS = DEPARTURE_WINDOW_HOURS * 60 * 60 * 1000
// inside this much of its departure, a not-ready vehicle is urgent rather
// than merely worth knowing about
const URGENT_WINDOW_MS = 4 * 60 * 60 * 1000
const MAX_NOT_READY_ROWS = 6

// ready to depart = washed/cleaned AND maintenance-cleared; anything else
// on a vehicle leaving inside the window is worth a look
const READY_VEHICLE_STATUSES = ['CLEANED', 'CLEAN']
const READY_MAINT_STATUSES = ['READY']

export default {
  name: 'widget_vehicle_maintenance',
  props: {
    tenant: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
      vehicles: [],
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // the heading states the window it is reporting on, read from the same
    // constant the filter uses, so the copy can't drift from the behavior
    departureWindowHours() {
      return DEPARTURE_WINDOW_HOURS
    },
    vsmRoute() {
      const tenant_type = (this.user.types && this.user.types[this.tenant]) || null
      const features = (this.user.features && this.user.features[this.tenant]) || {}
      if (tenant_type === 'FASTTRAK') return 'vehicle_status_dvir_fasttrak'
      return vsmRouteName(tenant_type, features)
    },
    pastDueCount() {
      return this.items.filter(row => Number(row.days_past_due) > 0).length
    },
    topRows() {
      return [...this.items]
        .sort((a, b) => Number(b.days_past_due || 0) - Number(a.days_past_due || 0))
        .slice(0, 5)
    },
    notReadyDepartures() {
      const cutoff = Date.now() + DEPARTURE_WINDOW_MS
      return this.vehicles
        .filter(v => {
          const nextMs = Number(v.next_mvmt_ms) * 1000
          if (!isFinite(nextMs) || nextMs <= 0 || nextMs > cutoff) return false
          const washOk = READY_VEHICLE_STATUSES.includes(String(v.vehicle_status || '').toUpperCase())
          const maintOk = READY_MAINT_STATUSES.includes(String(v.maintenance_status || '').toUpperCase())
          return !(washOk && maintOk)
        })
        .sort((a, b) => Number(a.next_mvmt_ms) - Number(b.next_mvmt_ms))
        .slice(0, MAX_NOT_READY_ROWS)
    },
  },
  created() {
    this.load()
  },
  methods: {
    washLabel(row) {
      return statusLabel(row.vehicle_status) || '—'
    },
    maintLabel(row) {
      return statusLabel(row.maintenance_status) || '—'
    },
    departsLabel(row) {
      if (row.next_mvmt_delta) {
        const delta = String(row.next_mvmt_delta)
        return delta.indexOf('late') === 0 ? delta : 'in ' + delta
      }
      return moment(Number(row.next_mvmt_ms) * 1000).fromNow()
    },
    // now that the window reaches 12 hours the rows are no longer uniformly
    // urgent, so the chip grades: red inside 4 hours (what the old window
    // covered), amber beyond it.
    departsChip(row) {
      const ms = Number(row.next_mvmt_ms) * 1000
      return (ms - Date.now()) <= URGENT_WINDOW_MS ? 'dw-chip-danger' : 'dw-chip-warning'
    },
    formatDate(value) {
      if (!value) return '—'
      const m = moment(String(value))
      return m.isValid() ? m.format('MM/DD') : String(value)
    },
    goToVsm() {
      this.$router.push({ name: this.vsmRoute }).catch(() => {})
    },
    goToMaintSummary() {
      this.$router.push({ name: 'vehicle_maint_summary' }).catch(() => {})
    },
    async load() {
      this.loading = true
      this.loadError = false
      // maint summary and vehicle statuses fail independently: losing one
      // list shouldn't blank the other, so only a double failure shows the
      // widget-level error state.
      const [maint, vehicles] = await Promise.all([
        get_vehicle_maint_summary(this.tenant).catch(() => null),
        get_vehicle_info(this.tenant, '', '', false).catch(() => null),
      ])
      this.items = (maint && maint.value && maint.value.data && maint.value.data.results) || []
      this.vehicles = (vehicles && vehicles.value && vehicles.value.data && vehicles.value.data.results) || []
      this.loadError = !maint && !vehicles
      this.loading = false
    },
  },
}
</script>
